import React from 'react'
import { Box, Typography, Paper, useTheme, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'
import { keyframes } from '@emotion/react'
import { FiStar } from 'react-icons/fi'

const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
`

const pulseRing = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
`

const iconBounce = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-10px) scale(1.05); }
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const progressFlow = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.7; }
  40% { transform: translateY(-10px); opacity: 1; }
`

const sparkle = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.2); }
`

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '500px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(4),
}))

const Orb = styled(Box)(({ size, color, delay, top, left, right, bottom }) => ({
  position: 'absolute',
  top: top || 'auto',
  left: left || 'auto',
  right: right || 'auto',
  bottom: bottom || 'auto',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(color, 0.4)} 0%, transparent 70%)`,
  filter: 'blur(40px)',
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: delay,
  pointerEvents: 'none',
  zIndex: 0,
}))

const ContentWrapper = styled(Box)({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  maxWidth: '500px',
  width: '100%',
})

const IconSection = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '32px',
})

const PulseCircle = styled(Box)(({ size, borderColor, delay = '0s' }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: size,
  height: size,
  border: `3px solid ${borderColor}`,
  borderRadius: '50%',
  animation: `${pulseRing} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
  animationDelay: delay,
}))

const MainIconPaper = styled(Paper)(({ theme, gradient }) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '3rem',
  position: 'relative',
  animation: `${iconBounce} 2s ease-in-out infinite`,
  boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
}))

const GradientTitle = styled(Typography)(({ gradient }) => ({
  fontWeight: 700,
  marginBottom: '12px',
  background: gradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
}))

const TimeBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  padding: '6px 16px',
  borderRadius: '32px',
  marginBottom: '32px',
  fontWeight: 600,
  fontSize: '0.9rem',
}))

const ProgressTrack = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 300,
  margin: '0 auto 20px',
  height: 8,
  backgroundColor: theme.palette.action.hover,
  borderRadius: 8,
  overflow: 'hidden',
  position: 'relative',
}))

const ProgressBar = styled(Box)(({ gradient }) => ({
  width: '100%',
  height: '100%',
  background: gradient,
  backgroundSize: '200% 100%',
  animation: `${progressFlow} 2s ease-in-out infinite`,
  position: 'relative',
  overflow: 'hidden',
}))

const ShimmerOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
  animation: `${shimmer} 2s ease-in-out infinite`,
})

const LoadingDot = styled(Box)(({ theme, gradient, delay }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: gradient,
  animation: `${dotBounce} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
}))

const GenerationLoader = React.memo(
  ({ type = 'default', elapsedSeconds = 0, message = null }) => {
    const theme = useTheme()

    const getTypeConfig = () => {
      switch (type) {
        case 'cad':
          return {
            title: 'Creazione del modello 3D',
            icon: '🏗️',
            description: 'Stiamo costruendo il tuo modello parametrico...',
          }
        case 'image':
          return {
            title: 'Creazione della tua immagine',
            icon: '🎨',
            description: "Stiamo generando la tua immagine con l'IA...",
          }
        case 'video':
          return {
            title: 'Creazione del tuo video',
            icon: '🎬',
            description:
              'Stiamo elaborando il tuo video. Potrebbe richiedere 1-2 minuti...',
          }
        case 'presentation':
          return {
            title: 'Generazione della presentazione',
            icon: '📊',
            description: 'Gamma sta preparando il deck richiesto...',
          }
        default:
          return {
            title: 'Elaborazione in corso',
            icon: '⚡',
            description: 'Stiamo elaborando la tua richiesta...',
          }
      }
    }

    const config = getTypeConfig()
    const displayMessage = message || config.description

    const mainGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${
      theme.palette.secondary.main || theme.palette.primary.dark
    } 100%)`

    return (
      <Container>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            inset: 0,
            pointerEvents: 'none',
          }}
        >
          <Orb
            color={theme.palette.primary.light}
            size={200}
            top="10%"
            left="10%"
            delay="0s"
          />
          <Orb
            color={theme.palette.secondary.light || theme.palette.primary.main}
            size={150}
            top="40%"
            right="10%"
            delay="2s"
          />
          <Orb
            color={theme.palette.info.light}
            size={180}
            bottom="10%"
            left="40%"
            delay="4s"
          />
        </Box>

        <ContentWrapper>
          <IconSection>
            <PulseCircle
              size={120}
              borderColor={alpha(theme.palette.primary.main, 0.3)}
            />
            <PulseCircle
              size={140}
              borderColor={alpha(
                theme.palette.secondary.main || theme.palette.primary.light,
                0.2
              )}
              delay="0.5s"
            />

            <MainIconPaper elevation={6} gradient={mainGradient}>
              <Box
                component="span"
                sx={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
              >
                {config.icon}
              </Box>
            </MainIconPaper>
          </IconSection>

          <GradientTitle variant="h4" gradient={mainGradient}>
            {config.title}
          </GradientTitle>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}
          >
            {displayMessage}
          </Typography>

          {elapsedSeconds > 0 && (
            <TimeBadge>
              <Box
                component={FiStar}
                sx={{ animation: `${sparkle} 1.5s ease-in-out infinite` }}
              />
              <span>Elaborazione: {Math.floor(elapsedSeconds)}s</span>
            </TimeBadge>
          )}

          <ProgressTrack>
            <ProgressBar gradient={mainGradient}>
              <ShimmerOverlay />
            </ProgressBar>
          </ProgressTrack>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {[0, 0.2, 0.4].map((delay, i) => (
              <LoadingDot key={i} gradient={mainGradient} delay={delay} />
            ))}
          </Box>
        </ContentWrapper>
      </Container>
    )
  }
)

export default GenerationLoader
